/**
 * useRadialMagneticLayout - Spring-driven magnetic dynamics on top of the
 * radial layout engine.
 *
 * Drives the cypher exploration graph. The base positions for every node come from
 * `LayoutNodesAtom` (written by `RadialLayoutEngine`). This hook:
 *
 *   1. Syncs a per-node dynamics state map (`MagneticDynamics`).
 *   2. Owns the rAF loop, integrating displacements toward their targets.
 *   3. Coalesces position writes into a single `setNodes` per frame,
 *      writing `position = base + disp` for every node.
 *   4. Exposes drag-event handlers + a `setMagneticTargets` callback so
 *      the visualizer can drive the pull-in on click.
 *
 * Edges (`FloatingEdge`) recompute geometry from node store positions
 * every render, so routing per-frame positions through `setNodes` keeps
 * edges coherent without extra work.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';

import { useReactFlow, useStore, useStoreApi, type Node } from '@xyflow/react';
import { bind } from 'bind-event-listener';
import { useAtomValue, useSetAtom } from 'jotai';

import { LayoutNodesAtom } from '../atoms/layoutAtoms';
import {
	APPEAR_TTL_MS,
	AppearingNodesAtom,
	HiddenNodesAtom,
	PruneAppearingAtom,
} from '../atoms/recencyAtoms';
import { computeAmbient, phaseIndexForId, prefersReducedMotion } from '../layout/AmbientWobble';
import {
	clearAllTargets,
	DEFAULT_DYNAMICS_CFG,
	integrate,
	seedNodeFromOrigin,
	setDragging,
	setTargets,
	syncDynState,
	type DynStateMap,
	type TargetSlot,
} from '../layout/MagneticDynamics';
import { estimateLayoutNodeSize } from '../nodeSizing';

// ============================================================================
// Types
// ============================================================================

export interface MagneticDragEvents {
	start: (event: React.MouseEvent, node: Node) => void;
	drag: (event: React.MouseEvent, node: Node) => void;
	stop: (event: React.MouseEvent, node: Node) => void;
}

export interface MagneticLayoutResult extends MagneticDragEvents {
	/** Replace every spring target. Pass [] to clear all targets. */
	setMagneticTargets: (slots: TargetSlot[]) => void;
	/** Zero every spring target so all nodes spring back to rest. */
	clearMagneticTargets: () => void;
	/** Prime new nodes so their first visible frame starts from an origin point. */
	primeNodesFromOrigin: (primes: Array<{ id: string; origin: { x: number; y: number } }>) => void;
	/**
	 * Declare a "spawn from" point for any nodes that *next* appear in
	 * `LayoutNodesAtom`. While a non-null origin is set, fresh nodes
	 * have their dynamics seeded with `disp = origin - rest` *inside*
	 * the same effect that ingests the new layout — eliminating any
	 * one-frame flash at rest before an external prime can run.
	 *
	 * Pass `null` to disable origin-seeding (e.g. on blur / pane click).
	 */
	setSelectionOrigin: (origin: { x: number; y: number } | null) => void;
	/**
	 * No-op stand-ins for compatibility with the previous force-layout
	 * surface (`pin` / `unpin` / `reheat`). The magnetic system has no
	 * notion of "reheat" — there's no temperature to nudge.
	 */
	pin: (nodeId: string) => void;
	unpin: (nodeId: string) => void;
	reheat: (alpha?: number) => void;
}

// ============================================================================
// Hook
// ============================================================================

export function useRadialMagneticLayout(): MagneticLayoutResult {
	const layoutNodes = useAtomValue(LayoutNodesAtom);
	const hiddenNodes = useAtomValue(HiddenNodesAtom);
	const appearingNodes = useAtomValue(AppearingNodesAtom);
	const pruneAppearing = useSetAtom(PruneAppearingAtom);
	const storeWidth = useStore((state) => state.width);
	const storeHeight = useStore((state) => state.height);
	const { setNodes, getNodes, setViewport } = useReactFlow();
	const storeApi = useStoreApi();

	// ── Refs (mutable state outside React's render cycle) ────────────
	const dynStateRef = useRef<DynStateMap>(new Map());
	const basePosRef = useRef<Map<string, { x: number; y: number }>>(new Map());
	const hiddenRef = useRef<Set<string>>(new Set());
	const rafRef = useRef<number | null>(null);
	const lastFrameTsRef = useRef<number | null>(null);
	const draggedIdRef = useRef<string | null>(null);
	/**
	 * Map of node ids that have been added to the layout mid-session but
	 * have not yet been measured by ReactFlow's ResizeObserver. While a
	 * node is in this map, the spring DOES NOT run for it:
	 *
	 *   • `integrate` skips it (no `disp` change).
	 *   • `projectNodes` skips it (no `setNodes` write that would echo
	 *     through the controlled-state round-trip).
	 *
	 * The user briefly sees the node at REST. As soon as the
	 * ResizeObserver delivers a real `measured.width`, the
	 * store-subscription effect below:
	 *
	 *   1. Looks up the captured origin (selection origin or auto-derived
	 *      landing origin from the time the node was registered).
	 *   2. Calls `seedNodeFromOrigin(dyn, id, restPosition, origin)` so
	 *      the node's `disp` jumps to `origin - rest`.
	 *   3. Removes the entry from `pendingMeasureRef`.
	 *   4. Synchronously projects, so the very next paint shows the node
	 *      at origin and the spring carries it back to rest.
	 *
	 * The captured `restPosition` is stored alongside the origin because
	 * by the time measurement arrives `LayoutNodesAtom` may have moved
	 * on (e.g. a sibling expansion); we want to spring out from the
	 * origin captured at the moment the node was first added.
	 *
	 * Why this matters: without per-node deferral, the spring runs
	 * before `measured` lands, every spring frame produces a new
	 * `graphNodes` array (via the controlled-state round-trip), and
	 * xyflow's prop-sync rebuilds `nodeLookup` from those arrays —
	 * wiping `measured` for the new node. That broke hover, click, and
	 * cursor for new nodes during the entire ~1-2s spring window.
	 */
	const pendingMeasureRef = useRef<
		Map<
			string,
			{
				origin: { x: number; y: number } | null;
				restPosition: { x: number; y: number };
			}
		>
	>(new Map());
	/**
	 * Optional origin point in graph-space. When set, any node added to
	 * `LayoutNodesAtom` while this ref is non-null gets its dynamics
	 * seeded so it visually starts at this origin and springs out to
	 * its rest/target. Cleared by callers via `setSelectionOrigin(null)`.
	 */
	const selectionOriginRef = useRef<{ x: number; y: number } | null>(null);
	/**
	 * Tracks whether we've already auto-fit the viewport for the
	 * current layout generation. Flipped true after the one-shot
	 * `fitView`; reset to false when the layout empties (ResetLayoutAtom)
	 * so a fresh seed triggers another fit.
	 */
	const hasFitViewRef = useRef(false);
	const DISCOVERY_MARGIN_X = 56;
	const DISCOVERY_MARGIN_Y = 44;

	// ── Ambient wobble state ─────────────────────────────────────────
	//
	// Each node gets a small per-frame drift on top of `base + disp`. The
	// drift is bounded inside a fraction of `RadialLayoutEngine`'s
	// collision padding (see `AmbientWobble.ts`) so it cannot cause two
	// nodes to overlap. The wobble is computed in `projectNodes` from
	// the timestamp captured by the rAF tick — `wobbleTimeSecRef`.
	//
	// `phaseIndexRef` caches a stable per-id phase so wobble is
	// reproducible across renders and de-correlated across nodes.
	const wobbleTimeSecRef = useRef<number>(0);
	const phaseIndexRef = useRef<Map<string, number>>(new Map());
	const reducedMotionRef = useRef<boolean>(false);

	function getPhaseIndex(id: string): number {
		const cache = phaseIndexRef.current;
		const cached = cache.get(id);
		if (cached !== undefined) return cached;
		const phase = phaseIndexForId(id);
		cache.set(id, phase);
		return phase;
	}

	const projectNodes = useCallback(
		(currentNodes: Node[]) => {
			const base = basePosRef.current;
			const dyn = dynStateRef.current;
			const hidden = hiddenRef.current;
			const pending = pendingMeasureRef.current;
			const lookup = storeApi.getState().nodeLookup as Map<
				string,
				{ measured?: { width: number; height: number } }
			>;

			const wobbleTime = wobbleTimeSecRef.current;
			const wobbleEnabled = !reducedMotionRef.current;

			return currentNodes.map((node) => {
				if (draggedIdRef.current === node.id) {
					return node;
				}
				// Don't touch unmeasured-yet nodes. See `pendingMeasureRef`
				// docstring above for why this is critical for newly-added
				// nodes (it stops the controlled-state round-trip that
				// clobbers ReactFlow's `measured` field mid-spring).
				if (pending.has(node.id)) {
					return node;
				}
				const b = base.get(node.id);
				if (!b) return node;
				const d = dyn.get(node.id);
				const dx = d?.dispX ?? 0;
				const dy = d?.dispY ?? 0;

				// Ambient wobble: bounded sin/cos drift added at projection
				// time only. Never folded into `base`, `disp`, or any
				// collision math — those continue to treat each node as
				// having a single static position. Suppressed while a
				// node is being dragged so the pointer feels exact.
				const ambient = computeAmbient(wobbleTime, getPhaseIndex(node.id), {
					enabled: wobbleEnabled,
					isDragged: d?.dragging === true,
				});

				const newX = b.x + dx + ambient.x;
				const newY = b.y + dy + ambient.y;
				const isHidden = hidden.has(node.id);

				if (
					node.position.x === newX &&
					node.position.y === newY &&
					(node.hidden ?? false) === isHidden
				) {
					return node;
				}

				// IMPORTANT: copy `measured` from the store's nodeLookup
				// (the source of truth that ReactFlow's ResizeObserver
				// writes to) onto every projected node — even if the
				// `node` argument we received doesn't carry it.
				//
				// Why: `useReactFlow().setNodes(updater)` runs in xyflow's
				// BatchProvider, which diffs the updater's output
				// (`items`) against `nodeLookup` and emits a `'replace'`
				// change for any node whose reference differs. The
				// `'replace'` then propagates through `applyNodeChanges`
				// in `useNodesState`'s `onNodesChange` handler, which
				// does `updatedElements.push({ ...change.item })`.
				// If `change.item` (our spread `node`) has no `measured`,
				// the new graphNodes entry has no `measured` either —
				// and on the next prop-sync, xyflow's `state.setNodes`
				// rebuilds `nodeLookup` from `graphNodes`, *wiping*
				// `measured`.
				//
				// By forwarding the latest store-known `measured` into
				// our spread, the resulting graphNodes entry keeps it,
				// and the next prop-sync round-trip preserves it. This
				// is the fix for the "newly-added nodes lose measured
				// mid-spring → hover dead, cursor stuck on `grab`,
				// click misses" regression (see
				// MEASURED_CLOBBER_ANALYSIS.md).
				const storeMeasured = lookup.get(node.id)?.measured;
				const next: Node = {
					...node,
					position: { x: newX, y: newY },
					hidden: isHidden,
				};
				if (storeMeasured && (next as Node & { measured?: unknown }).measured === undefined) {
					(next as Node & { measured?: { width: number; height: number } }).measured =
						storeMeasured;
				}
				return next;
			});
		},
		[storeApi],
	);

	// ── Sync base positions + dyn state when LayoutNodesAtom changes ─
	//
	// To keep blur / re-layout transitions smooth (rather than snapping
	// nodes to their new base in one frame) we seed each surviving
	// node's `disp` so the *rendered* position (`base + disp`) is
	// identical immediately before and after the swap. Since `target`
	// stays at 0 during a blur relax, the integrator then carries the
	// node from its old visual position back to the new base via the
	// usual spring → "spring-interpolate to new base".
	// `useLayoutEffect` (not `useEffect`) so disp seeds + base swap apply
	// *before* the browser paints. Without this, the sync effect that
	// pushes `LayoutNodesAtom` rest positions into ReactFlow can land
	// first → ReactFlow paints children at rest → then this effect runs
	// → then the rAF loop applies disp → producing a one-frame "rest
	// flash" before the spring takes over.
	useLayoutEffect(() => {
		const oldBase = basePosRef.current;
		const newBase = new Map<string, { x: number; y: number }>();
		for (const [id, node] of layoutNodes) {
			newBase.set(id, { x: node.x, y: node.y });
		}

		const dyn = dynStateRef.current;
		syncDynState(dyn, Array.from(newBase.keys()));

		// `landingOrigin`: when this is the *first* commit that brings nodes
		// into the layout (oldBase empty, newBase non-empty) AND no caller
		// has explicitly set `selectionOriginRef`, auto-derive the origin
		// from the layout root (the parent-less node — typically the user)
		// so all neighbours visually spawn from the user and spring out to
		// the ring. This is the same pull-out animation a click on any node
		// produces, applied to the landing experience without requiring the
		// user node to be "selected".
		let landingOrigin: { x: number; y: number } | null = null;
		if (oldBase.size === 0 && newBase.size > 0 && !selectionOriginRef.current) {
			let rootId: string | null = null;
			for (const [id, ln] of layoutNodes) {
				if (!ln.parentId) {
					rootId = id;
					break;
				}
			}
			if (rootId) {
				const rootRest = newBase.get(rootId);
				if (rootRest) {
					const rootSize = estimateLayoutNodeSize(layoutNodes.get(rootId)!.sizingInput);
					landingOrigin = {
						x: rootRest.x + rootSize.width / 2,
						y: rootRest.y + rootSize.height / 2,
					};
				}
			}
		}

		const origin = selectionOriginRef.current ?? landingOrigin;

		for (const [id, np] of newBase) {
			const op = oldBase.get(id);
			if (!op) {
				// Freshly-added node.
				//
				// Initial load (`oldBase.size === 0`): if an `origin` is
				// available we seed the node's `disp` directly so the
				// very first paint renders it at `origin` and the spring
				// carries it out to `rest`. We do NOT defer — the whole
				// graph would otherwise stay invisible until every node
				// is measured. The root node naturally has zero
				// displacement when seeded from its own centre.
				//
				// Mid-session expansion (`oldBase.size > 0`): defer the
				// origin-seed entirely. We register the id in
				// `pendingMeasureRef` capturing both the origin to use
				// once measurement arrives and the rest position
				// (`np`) so we can compute the seed displacement when
				// the time comes — `LayoutNodesAtom` may have moved on
				// by then. The node briefly paints at `np` (rest) and
				// then springs out to its target as soon as
				// ResizeObserver delivers `measured`. See
				// `pendingMeasureRef` docstring for the full rationale
				// (stops the prop-sync clobber that was breaking
				// hover/click/cursor mid-spring).
				if (oldBase.size === 0) {
					if (origin) {
						seedNodeFromOrigin(dyn, id, np, origin);
					}
				} else {
					pendingMeasureRef.current.set(id, {
						origin,
						restPosition: { x: np.x, y: np.y },
					});
				}
				continue;
			}
			const dx = op.x - np.x;
			const dy = op.y - np.y;
			if (dx === 0 && dy === 0) continue;
			const entry = dyn.get(id);
			if (!entry) continue;
			entry.dispX += dx;
			entry.dispY += dy;
		}

		basePosRef.current = newBase;

		// Prune the wobble phase cache so it doesn't grow unbounded as
		// nodes appear and disappear over a long session. Phases for
		// re-added ids are deterministically rederived on next read.
		const phaseCache = phaseIndexRef.current;
		if (phaseCache.size > 0) {
			for (const cachedId of phaseCache.keys()) {
				if (!newBase.has(cachedId)) phaseCache.delete(cachedId);
			}
		}

		if (newBase.size === 0) {
			hasFitViewRef.current = false;
			wasSpringActiveRef.current = false;
			onSpringSettledRef.current = null;
		}
	}, [layoutNodes]);

	useEffect(() => {
		if (layoutNodes.size === 0 || hasFitViewRef.current) {
			return;
		}
		if (storeWidth <= 0 || storeHeight <= 0) {
			return;
		}

		let rootId: string | null = null;
		for (const [id, node] of layoutNodes) {
			if (!node.parentId) {
				rootId = id;
				break;
			}
		}
		if (!rootId) {
			rootId = layoutNodes.keys().next().value ?? null;
		}
		if (!rootId) {
			return;
		}

		// Mark as fitted immediately so this effect doesn't re-fire on
		// subsequent `layoutNodes` changes (e.g. from AppearingNodesAtom
		// ticks). The actual viewport call is deferred until the spring
		// settles so we fit to *final* node positions, not to the
		// collapsed-at-origin first-paint positions.
		hasFitViewRef.current = true;

		// Capture a stable snapshot of the values the callback needs.
		const capturedRootId = rootId;
		const capturedStoreW = storeWidth;
		const capturedStoreH = storeHeight;

		const doFitView = () => {
			const rfNodes = getNodes();
			const rootNode = rfNodes.find((node) => node.id === capturedRootId);
			if (!rootNode) {
				return;
			}

			const halfAvailableW = Math.max(capturedStoreW / 2 - DISCOVERY_MARGIN_X, 1);
			const halfAvailableH = Math.max(capturedStoreH / 2 - DISCOVERY_MARGIN_Y, 1);

			const fallbackSizeFor = (nodeId: string) => {
				const layoutNode = layoutNodes.get(nodeId);
				return layoutNode
					? estimateLayoutNodeSize(layoutNode.sizingInput)
					: { width: 220, height: 64 };
			};

			const rootFallbackSize = fallbackSizeFor(capturedRootId);
			const rootWidth =
				rootNode.measured?.width ??
				(typeof rootNode.width === 'number' ? rootNode.width : rootFallbackSize.width);
			const rootHeight =
				rootNode.measured?.height ??
				(typeof rootNode.height === 'number' ? rootNode.height : rootFallbackSize.height);
			const rootCenterX = rootNode.position.x + rootWidth / 2;
			const rootCenterY = rootNode.position.y + rootHeight / 2;

			let leftExtent = 0;
			let rightExtent = 0;
			let topExtent = 0;
			let bottomExtent = 0;

			for (const node of rfNodes) {
				const fallbackSize = fallbackSizeFor(node.id);
				const width =
					node.measured?.width ??
					(typeof node.width === 'number' ? node.width : fallbackSize.width);
				const height =
					node.measured?.height ??
					(typeof node.height === 'number' ? node.height : fallbackSize.height);
				const minX = node.position.x;
				const maxX = node.position.x + width;
				const minY = node.position.y;
				const maxY = node.position.y + height;

				leftExtent = Math.max(leftExtent, rootCenterX - minX);
				rightExtent = Math.max(rightExtent, maxX - rootCenterX);
				topExtent = Math.max(topExtent, rootCenterY - minY);
				bottomExtent = Math.max(bottomExtent, maxY - rootCenterY);
			}

			const safeRatio = (available: number, extent: number) =>
				extent > 0 ? available / extent : 1;
			const zoom = Math.min(
				1,
				safeRatio(halfAvailableW, leftExtent),
				safeRatio(halfAvailableW, rightExtent),
				safeRatio(halfAvailableH, topExtent),
				safeRatio(halfAvailableH, bottomExtent),
			);

			setViewport(
				{
					x: capturedStoreW / 2 - rootCenterX * zoom,
					y: capturedStoreH / 2 - rootCenterY * zoom,
					zoom,
				},
				{ duration: 600 },
			);
		};

		// Register the fitView to fire once the spring has settled so it
		// uses the final resting node positions (not the collapsed-origin
		// first-paint positions from the landing animation).
		//
		// Fallback: if the spring never becomes active (e.g. a layout
		// with zero displacement because no landing animation ran), we
		// still need to fit the view. We use a 200 ms timeout — long
		// enough for the rAF loop to have fired at least one integration
		// tick (and thus set `wasSpringActiveRef = true`) if the spring
		// is active, but short enough not to feel sluggish on static
		// layouts. If the callback fires after the spring already ran,
		// `onSpringSettledRef` will be null and the timeout is a no-op.
		const fallbackTimer = setTimeout(() => {
			if (onSpringSettledRef.current) {
				onSpringSettledRef.current = null;
				doFitView();
			}
		}, 200);

		onSpringSettledRef.current = () => {
			// Spring settled — cancel the fallback and fit to final positions.
			clearTimeout(fallbackTimer);
			onSpringSettledRef.current = null;
			doFitView();
		};

		return () => {
			// Cleanup: if the effect re-fires or the component unmounts
			// before either path runs, cancel both.
			clearTimeout(fallbackTimer);
			onSpringSettledRef.current = null;
		};
	}, [layoutNodes, getNodes, setViewport, storeHeight, storeWidth]);

	// Mirror hidden set into a ref so the rAF loop / setNodes writer
	// can read it without re-binding the effect.
	useEffect(() => {
		hiddenRef.current = hiddenNodes;
	}, [hiddenNodes]);

	// Schedule a prune for any appearing entries shortly after they
	// land. The atom drives a one-shot opacity fade on mount, so once
	// the TTL elapses we remove the entries to keep the map bounded
	// and to ensure consumers see `isAppearing === false` from then on.
	useEffect(() => {
		if (appearingNodes.size === 0) return;
		const timeout = setTimeout(() => pruneAppearing(), APPEAR_TTL_MS + 50);
		return () => clearTimeout(timeout);
	}, [appearingNodes, pruneAppearing]);

	// ── Spring-settled callback ───────────────────────────────────────
	// Called the first time the rAF loop transitions from active → idle
	// after a new layout has been committed. Used to trigger fitView
	// at final (settled) node positions rather than at first-paint
	// positions (which would fit to the spring's starting point).
	const onSpringSettledRef = useRef<(() => void) | null>(null);
	const wasSpringActiveRef = useRef(false);

	// ── rAF loop ─────────────────────────────────────────────────────
	useEffect(() => {
		// Track `prefers-reduced-motion` so the ambient wobble can be
		// disabled live when the user toggles their OS setting.
		reducedMotionRef.current = prefersReducedMotion();
		let unbindReducedMotionChange: (() => void) | null = null;
		const handleReducedMotionChange = () => {
			reducedMotionRef.current = prefersReducedMotion();
		};
		if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
			try {
				const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
				unbindReducedMotionChange = bind(mediaQuery, {
					type: 'change',
					listener: handleReducedMotionChange,
				});
			} catch {
				unbindReducedMotionChange = null;
			}
		}

		const tick = (ts: number) => {
			rafRef.current = requestAnimationFrame(tick);

			const last = lastFrameTsRef.current;
			lastFrameTsRef.current = ts;
			if (last === null) return;

			const dtSec = (ts - last) / 1000;

			// Advance the wobble clock every frame so the projector
			// reads a fresh `t` regardless of whether springs are active.
			wobbleTimeSecRef.current = ts / 1000;

			const wasActive = integrate(
				dynStateRef.current,
				DEFAULT_DYNAMICS_CFG,
				dtSec,
				pendingMeasureRef.current,
			);

			// We need to project on every frame (not only when springs
			// are active) so the ambient wobble keeps the canvas alive
			// at rest. Skip projection only if there is literally
			// nothing to draw (no base positions registered yet) and
			// wobble is disabled — otherwise the early-equality bail-out
			// inside `projectNodes` already keeps the work cheap when
			// nothing changed.
			const wobbleOn = !reducedMotionRef.current;
			const haveBases = basePosRef.current.size > 0;
			if (wasActive || (wobbleOn && haveBases)) {
				setNodes((currentNodes) => projectNodes(currentNodes));
			}

			if (wasActive) {
				wasSpringActiveRef.current = true;
			} else if (wasSpringActiveRef.current) {
				// Spring just settled (active → idle transition).
				wasSpringActiveRef.current = false;
				onSpringSettledRef.current?.();
			}
		};

		rafRef.current = requestAnimationFrame(tick);
		return () => {
			if (rafRef.current !== null) {
				cancelAnimationFrame(rafRef.current);
				rafRef.current = null;
			}
			lastFrameTsRef.current = null;
			unbindReducedMotionChange?.();
		};
	}, [projectNodes, setNodes]);

	// ── Measurement subscription ──────────────────────────────────────
	// Subscribe to the ReactFlow store and, whenever it changes, walk
	// `pendingMeasureRef` looking for nodes that now have a non-zero
	// `measured.width`. For each one:
	//
	//   1. Look up the captured `origin` + `restPosition` registered at
	//      the time the node was first added to the layout.
	//   2. If an origin was captured, seed the node's spring `disp` so
	//      its very next paint renders at `origin` (not at `rest`).
	//   3. Remove the entry from `pendingMeasureRef` so the next rAF
	//      tick begins integrating + projecting it normally.
	//   4. Synchronously call `setNodes((nodes) => projectNodes(nodes))`
	//      so the seeded `disp` lands in ReactFlow's store before the
	//      next paint — the user sees the node fly OUT from origin
	//      rather than appearing at REST and then jumping to origin.
	//
	// Using `storeApi.subscribe` rather than `useStore` so the effect
	// fires on every internal store update (including the dimensions
	// change from ReactFlow's ResizeObserver), without triggering a
	// React re-render of the component owning this hook.
	useEffect(() => {
		const unsubscribe = storeApi.subscribe((state) => {
			if (pendingMeasureRef.current.size === 0) return;
			const lookup = state.nodeLookup as Map<string, { measured?: { width?: number } }>;
			const ready: Array<
				[
					string,
					{ origin: { x: number; y: number } | null; restPosition: { x: number; y: number } },
				]
			> = [];
			for (const [id, entry] of pendingMeasureRef.current) {
				const m = lookup.get(id)?.measured;
				if (m && typeof m.width === 'number' && m.width > 0) {
					ready.push([id, entry]);
				}
			}
			if (ready.length === 0) return;
			let didSeed = false;
			for (const [id, entry] of ready) {
				pendingMeasureRef.current.delete(id);
				// Seed the spring disp from the captured origin (if any).
				// Using the captured `restPosition` rather than the
				// current `basePosRef.current.get(id)` because by now
				// the layout may have re-flowed (e.g. a sibling
				// expansion); the `origin → restPosition` displacement
				// captured at registration time is the one we want.
				if (entry.origin) {
					seedNodeFromOrigin(dynStateRef.current, id, entry.restPosition, entry.origin);
					didSeed = true;
				}
			}
			if (didSeed) {
				// Synchronously project so the seeded `disp` (which puts
				// the node at `origin`) is committed to the store before
				// the next paint. From here the rAF tick takes over and
				// decays `disp` toward zero — the user sees the node
				// fly out from origin to its rest slot. Without this
				// sync write, the next paint might still show the node
				// at `rest` until the rAF tick fires.
				setNodes((currentNodes) => projectNodes(currentNodes));
			}
		});
		return unsubscribe;
	}, [storeApi, setNodes, projectNodes]);

	// ── Topology / hidden change → push base positions immediately ───
	// The rAF loop only writes when dynamics are active, so when the
	// graph topology changes (new nodes added, hidden flag toggled) we
	// need a one-shot sync to make sure the new nodes appear at their
	// base + disp positions and the hidden flag reaches ReactFlow.
	//
	// `useLayoutEffect` so the projected positions (with seed-from-origin
	// disp baked in) are written *before* the browser paints — eliminating
	// any one-frame flash at rest while the rAF loop spins up.
	useLayoutEffect(() => {
		setNodes((currentNodes) => projectNodes(currentNodes));
	}, [layoutNodes, hiddenNodes, projectNodes, setNodes]);

	// ── Public API ───────────────────────────────────────────────────
	const setMagneticTargets = useCallback((slots: TargetSlot[]) => {
		setTargets(dynStateRef.current, slots);
	}, []);

	const clearMagneticTargets = useCallback(() => {
		clearAllTargets(dynStateRef.current);
	}, []);

	const primeNodesFromOrigin = useCallback(
		(primes: Array<{ id: string; origin: { x: number; y: number } }>) => {
			if (primes.length === 0) {
				return;
			}

			const base = basePosRef.current;
			const dyn = dynStateRef.current;
			for (const prime of primes) {
				const restPosition = base.get(prime.id);
				if (!restPosition) continue;

				if (pendingMeasureRef.current.has(prime.id)) {
					// Node hasn't been measured yet — update the captured
					// origin so the deferred seed-on-measurement uses
					// this newer one. The actual seedNodeFromOrigin call
					// will run inside the measurement-subscription
					// effect once `measured` arrives.
					pendingMeasureRef.current.set(prime.id, {
						origin: prime.origin,
						restPosition: { x: restPosition.x, y: restPosition.y },
					});
					continue;
				}

				// Already measured — seed immediately and project so the
				// fly-in starts on the next paint.
				seedNodeFromOrigin(dyn, prime.id, restPosition, prime.origin);
			}

			setNodes((currentNodes) => projectNodes(currentNodes));
		},
		[projectNodes, setNodes],
	);

	const setSelectionOrigin = useCallback((origin: { x: number; y: number } | null) => {
		selectionOriginRef.current = origin;
	}, []);

	const dragEvents = useMemo<MagneticDragEvents>(
		() => ({
			start: (_event, node) => {
				draggedIdRef.current = node.id;
				setDragging(dynStateRef.current, node.id, true);
			},
			drag: (_event, node) => {
				// We let ReactFlow update the node's `position` in store
				// directly; on stop, that position becomes the new base.
				draggedIdRef.current = node.id;
			},
			stop: (_event, node) => {
				// Bounce-back: the node's atom rest position
				// (`basePosRef`) is intentionally NOT updated. We seed
				// the dynamics with `disp = dragEndPos - rest` so the
				// node's visual position stays at the drop point on the
				// very next paint, then the spring carries it smoothly
				// back to whatever target the magnetic system currently
				// has for this node.
				//
				// IMPORTANT: do NOT zero `targetX/targetY` here. The
				// spring target represents the node's current "where it
				// wants to be" offset from rest — in selected/focus
				// mode this is the radial pull-in position written by
				// `setMagneticTargets`. Wiping it on drop would make
				// the node spring back to its un-selected rest position
				// instead of the radial slot it occupies in selected
				// mode. Preserving the existing target lets the spring
				// carry the node back to the correct (rest or radial)
				// position depending on the current selection state.
				const base = basePosRef.current.get(node.id);
				const entry = dynStateRef.current.get(node.id);
				if (base && entry) {
					entry.dispX = node.position.x - base.x;
					entry.dispY = node.position.y - base.y;
					entry.vx = 0;
					entry.vy = 0;
				}
				setDragging(dynStateRef.current, node.id, false);
				draggedIdRef.current = null;
			},
		}),
		[],
	);

	const pin = useCallback((_nodeId: string) => {
		// no-op: hover pinning has no analogue in the magnetic system.
	}, []);

	const unpin = useCallback((_nodeId: string) => {
		// no-op
	}, []);

	const reheat = useCallback((_alpha?: number) => {
		// no-op: nothing to reheat in the spring system.
	}, []);

	return {
		...dragEvents,
		setMagneticTargets,
		clearMagneticTargets,
		primeNodesFromOrigin,
		setSelectionOrigin,
		pin,
		unpin,
		reheat,
	};
}
